import { FormCard } from "@/components/FormCard"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldRow,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  emissionTestModes,
  type EmissionTestMode,
  type TestsTabState,
} from "@/lib/documentEditor"

interface GlassPane {
  key: "windshield" | "left" | "right" | "rear"
  exemptKey: "windshieldExempt" | "leftExempt" | "rightExempt" | "rearExempt"
  label: string
}

const glassPanes: GlassPane[] = [
  { key: "windshield", exemptKey: "windshieldExempt", label: "Вітрове скло" },
  { key: "left", exemptKey: "leftExempt", label: "Бокове ліве скло" },
  { key: "right", exemptKey: "rightExempt", label: "Бокове праве скло" },
  { key: "rear", exemptKey: "rearExempt", label: "Заднє скло" },
]

function GlassPaneBlock({
  pane,
  value,
  errors,
  onChange,
  onCommit,
}: {
  pane: GlassPane
  value: TestsTabState
  errors: Partial<Record<keyof TestsTabState, string>>
  onChange: (patch: Partial<TestsTabState>) => void
  onCommit: (patch: Partial<TestsTabState>) => void
}) {
  const exempt = value[pane.exemptKey]
  const error = errors[pane.key]
  return (
    <div className="flex-1 rounded-lg border p-3">
      <p className="text-sm font-medium">{pane.label}</p>
      <div className="mt-2 flex items-start gap-2">
        <Checkbox
          checked={exempt}
          id={`glass-exempt-${pane.key}`}
          onCheckedChange={(checked) => {
            const patch = { [pane.exemptKey]: checked === true } as Partial<TestsTabState>
            onChange(patch)
            onCommit(patch)
          }}
        />
        <label
          className="text-xs text-muted-foreground"
          htmlFor={`glass-exempt-${pane.key}`}
        >
          має маркування R43 та відсутній додатковий покрив
        </label>
      </div>
      {!exempt && (
        <Field className="mt-3" controlLength="3x" data-invalid={Boolean(error)}>
          <FieldLabel htmlFor={`glass-${pane.key}`}>
            Світлопропускання, %
          </FieldLabel>
          <Input
            aria-invalid={Boolean(error)}
            id={`glass-${pane.key}`}
            max={100}
            min={0}
            onBlur={() =>
              onCommit({ [pane.key]: value[pane.key] } as Partial<TestsTabState>)
            }
            onChange={(e) =>
              onChange({ [pane.key]: e.target.value } as Partial<TestsTabState>)
            }
            type="number"
            value={value[pane.key]}
          />
          {error && <FieldError>{error}</FieldError>}
        </Field>
      )}
    </div>
  )
}

export function TestsTab({
  value,
  errors,
  onChange,
  onCommit,
}: {
  value: TestsTabState
  /** Per-field messages from shared's `editableDraftFields` — see DocumentEdit.tsx's `fieldErrors`. Only set for fields a free-text input could actually get wrong (the Select-backed `emissionMode` and the exempt checkboxes can't). */
  errors: Partial<Record<keyof TestsTabState, string>>
  onChange: (patch: Partial<TestsTabState>) => void
  /** Autosaves a patch immediately (select/checkbox fields) — text fields autosave on blur instead. */
  onCommit: (patch: Partial<TestsTabState>) => void
}) {
  return (
    <div className="flex flex-col gap-4">
      <FormCard title="Результати випробувань">
        <Field data-invalid={Boolean(errors.videoUrl)}>
          <FieldLabel htmlFor="tests-video-url">
            Посилання на відеофайл щодо відеофіксації процесу ОТК
          </FieldLabel>
          <Input
            aria-invalid={Boolean(errors.videoUrl)}
            id="tests-video-url"
            onBlur={() => onCommit({ videoUrl: value.videoUrl })}
            onChange={(e) => onChange({ videoUrl: e.target.value })}
            placeholder="https://..."
            value={value.videoUrl}
          />
          {errors.videoUrl && <FieldError>{errors.videoUrl}</FieldError>}
        </Field>
      </FormCard>

      <FormCard title="Характеристики гальмової системи">
        <FieldGroup className="gap-4">
          <FieldRow>
            <Field
              controlLength="3x"
              data-invalid={Boolean(errors.totalBrakingForce)}
              length="full-size"
              required
            >
              <FieldLabel htmlFor="tests-total-braking-force">
                Загальна питома гальмова сила
              </FieldLabel>
              <Input
                aria-invalid={Boolean(errors.totalBrakingForce)}
                id="tests-total-braking-force"
                onBlur={() =>
                  onCommit({ totalBrakingForce: value.totalBrakingForce })
                }
                onChange={(e) =>
                  onChange({ totalBrakingForce: e.target.value })
                }
                type="number"
                value={value.totalBrakingForce}
              />
              {errors.totalBrakingForce && (
                <FieldError>{errors.totalBrakingForce}</FieldError>
              )}
            </Field>
          </FieldRow>
          <FieldRow>
            <Field
              controlLength="3x"
              data-invalid={Boolean(errors.imbalanceRatio)}
              length="full-size"
              required
            >
              <FieldLabel htmlFor="tests-imbalance-ratio">
                Коефіцієнт нерівномірності гальмових сил (найбільший показник)
              </FieldLabel>
              <Input
                aria-invalid={Boolean(errors.imbalanceRatio)}
                id="tests-imbalance-ratio"
                onBlur={() =>
                  onCommit({ imbalanceRatio: value.imbalanceRatio })
                }
                onChange={(e) => onChange({ imbalanceRatio: e.target.value })}
                type="number"
                value={value.imbalanceRatio}
              />
              {errors.imbalanceRatio && (
                <FieldError>{errors.imbalanceRatio}</FieldError>
              )}
            </Field>
          </FieldRow>
          <FieldRow>
            <Field
              controlLength="3x"
              data-invalid={Boolean(errors.brakeResponseTime)}
              length="full-size"
              required
            >
              <FieldLabel htmlFor="tests-brake-response-time">
                Тривалість спрацювання робочої гальмової системи (сек.)
              </FieldLabel>
              <Input
                aria-invalid={Boolean(errors.brakeResponseTime)}
                id="tests-brake-response-time"
                onBlur={() =>
                  onCommit({ brakeResponseTime: value.brakeResponseTime })
                }
                onChange={(e) =>
                  onChange({ brakeResponseTime: e.target.value })
                }
                type="number"
                value={value.brakeResponseTime}
              />
              {errors.brakeResponseTime && (
                <FieldError>{errors.brakeResponseTime}</FieldError>
              )}
            </Field>
          </FieldRow>
          <FieldRow>
            <Field
              controlLength="3x"
              data-invalid={Boolean(errors.parkingBrakeForce)}
              length="full-size"
              required
            >
              <FieldLabel htmlFor="tests-parking-brake-force">
                Питома гальмова сила стоянкової гальмової системи
              </FieldLabel>
              <Input
                aria-invalid={Boolean(errors.parkingBrakeForce)}
                id="tests-parking-brake-force"
                onBlur={() =>
                  onCommit({ parkingBrakeForce: value.parkingBrakeForce })
                }
                onChange={(e) =>
                  onChange({ parkingBrakeForce: e.target.value })
                }
                type="number"
                value={value.parkingBrakeForce}
              />
              {errors.parkingBrakeForce && (
                <FieldError>{errors.parkingBrakeForce}</FieldError>
              )}
            </Field>
          </FieldRow>
          <FieldRow>
            <Field
              controlLength="3x"
              data-invalid={Boolean(errors.ovality)}
              length="full-size"
            >
              <FieldLabel htmlFor="tests-ovality">
                Коливання значень гальмівних сил під час кожного повного
                оберту колеса (овальність, %)
              </FieldLabel>
              <Input
                aria-invalid={Boolean(errors.ovality)}
                id="tests-ovality"
                onBlur={() => onCommit({ ovality: value.ovality })}
                onChange={(e) => onChange({ ovality: e.target.value })}
                type="number"
                value={value.ovality}
              />
              {errors.ovality && <FieldError>{errors.ovality}</FieldError>}
            </Field>
          </FieldRow>
        </FieldGroup>
      </FormCard>

      <FormCard title="Результати випробування спалин ТЗ">
        <FieldGroup className="gap-4">
          <FieldRow>
            <Field>
              <FieldLabel htmlFor="tests-emission-mode">
                Тип випробування спалин
              </FieldLabel>
              <Select
                onValueChange={(next) => {
                  const emissionMode = next as EmissionTestMode
                  onChange({ emissionMode })
                  onCommit({ emissionMode })
                }}
                value={value.emissionMode}
              >
                <SelectTrigger id="tests-emission-mode" length="full-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {emissionTestModes.map((mode) => (
                    <SelectItem key={mode.value} value={mode.value}>
                      {mode.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </FieldRow>
          {value.emissionMode === "PETROL_GAS" && (
            <>
              <FieldRow>
                <Field controlLength="3x" data-invalid={Boolean(errors.coIdle)} length="6x">
                  <FieldLabel htmlFor="tests-co-idle">
                    CO (%) при мінімальній частоті
                  </FieldLabel>
                  <Input
                    aria-invalid={Boolean(errors.coIdle)}
                    id="tests-co-idle"
                    onBlur={() => onCommit({ coIdle: value.coIdle })}
                    onChange={(e) => onChange({ coIdle: e.target.value })}
                    type="number"
                    value={value.coIdle}
                  />
                  {errors.coIdle && <FieldError>{errors.coIdle}</FieldError>}
                </Field>
                <Field controlLength="3x" data-invalid={Boolean(errors.cnhmIdle)} length="6x">
                  <FieldLabel htmlFor="tests-cnhm-idle">
                    CnHm (млн⁻¹) при мінімальній частоті
                  </FieldLabel>
                  <Input
                    aria-invalid={Boolean(errors.cnhmIdle)}
                    id="tests-cnhm-idle"
                    onBlur={() => onCommit({ cnhmIdle: value.cnhmIdle })}
                    onChange={(e) => onChange({ cnhmIdle: e.target.value })}
                    type="number"
                    value={value.cnhmIdle}
                  />
                  {errors.cnhmIdle && <FieldError>{errors.cnhmIdle}</FieldError>}
                </Field>
              </FieldRow>
              <FieldRow>
                <Field controlLength="3x" data-invalid={Boolean(errors.coElevated)} length="6x">
                  <FieldLabel htmlFor="tests-co-elevated">
                    CO (%) при підвищеній частоті
                  </FieldLabel>
                  <Input
                    aria-invalid={Boolean(errors.coElevated)}
                    id="tests-co-elevated"
                    onBlur={() => onCommit({ coElevated: value.coElevated })}
                    onChange={(e) =>
                      onChange({ coElevated: e.target.value })
                    }
                    type="number"
                    value={value.coElevated}
                  />
                  {errors.coElevated && (
                    <FieldError>{errors.coElevated}</FieldError>
                  )}
                </Field>
                <Field
                  controlLength="3x"
                  data-invalid={Boolean(errors.cnhmElevated)}
                  length="6x"
                >
                  <FieldLabel htmlFor="tests-cnhm-elevated">
                    CnHm (млн⁻¹) при підвищеній частоті
                  </FieldLabel>
                  <Input
                    aria-invalid={Boolean(errors.cnhmElevated)}
                    id="tests-cnhm-elevated"
                    onBlur={() =>
                      onCommit({ cnhmElevated: value.cnhmElevated })
                    }
                    onChange={(e) =>
                      onChange({ cnhmElevated: e.target.value })
                    }
                    type="number"
                    value={value.cnhmElevated}
                  />
                  {errors.cnhmElevated && (
                    <FieldError>{errors.cnhmElevated}</FieldError>
                  )}
                </Field>
              </FieldRow>
              <FieldRow>
                <Field controlLength="3x" data-invalid={Boolean(errors.lambda)} length="6x">
                  <FieldLabel htmlFor="tests-lambda">
                    Коефіцієнт надміру повітря, λ
                  </FieldLabel>
                  <Input
                    aria-invalid={Boolean(errors.lambda)}
                    id="tests-lambda"
                    onBlur={() => onCommit({ lambda: value.lambda })}
                    onChange={(e) => onChange({ lambda: e.target.value })}
                    type="number"
                    value={value.lambda}
                  />
                  {errors.lambda && <FieldError>{errors.lambda}</FieldError>}
                </Field>
              </FieldRow>
            </>
          )}
          {value.emissionMode === "DIESEL" && (
            <FieldRow>
              <Field data-invalid={Boolean(errors.smokeK)}>
                <FieldLabel htmlFor="tests-smoke-k">
                  Димність спалин ТЗ з дизелем або газодизелем K (м⁻¹)
                </FieldLabel>
                <Input
                  aria-invalid={Boolean(errors.smokeK)}
                  id="tests-smoke-k"
                  onBlur={() => onCommit({ smokeK: value.smokeK })}
                  onChange={(e) => onChange({ smokeK: e.target.value })}
                  type="number"
                  value={value.smokeK}
                />
                {errors.smokeK && <FieldError>{errors.smokeK}</FieldError>}
              </Field>
            </FieldRow>
          )}
        </FieldGroup>
      </FormCard>

      <FormCard title="Результати випробувань світлопропускання стекол ТЗ">
        <p className="mb-3 text-xs text-muted-foreground">
          крім стекол з наявним маркуванням відповідності технічним приписам
          R43 та відсутнім додатковим покривом
        </p>
        <FieldGroup className="gap-4">
          <FieldRow>
            <GlassPaneBlock
              errors={errors}
              onChange={onChange}
              onCommit={onCommit}
              pane={glassPanes[0]}
              value={value}
            />
          </FieldRow>
          <FieldRow>
            <GlassPaneBlock
              errors={errors}
              onChange={onChange}
              onCommit={onCommit}
              pane={glassPanes[1]}
              value={value}
            />
            <GlassPaneBlock
              errors={errors}
              onChange={onChange}
              onCommit={onCommit}
              pane={glassPanes[2]}
              value={value}
            />
          </FieldRow>
          <FieldRow>
            <GlassPaneBlock
              errors={errors}
              onChange={onChange}
              onCommit={onCommit}
              pane={glassPanes[3]}
              value={value}
            />
          </FieldRow>
        </FieldGroup>
      </FormCard>
    </div>
  )
}
